import { useState, useEffect } from 'react';
import { supabase } from '../../../../lib/supabaseClient';
import { BlogPost, BlogCategory, BlogPostFormData, BlogPostStatus } from '../../../../types/blog';
import ImageUploader from '../../../../components/base/ImageUploader';
import HelpModal, { HelpStep } from '../../../../components/feature/HelpModal';

const BLOG_HELP_STEPS: HelpStep[] = [
  {
    icon: 'ri-toggle-line',
    title: '公開・下書きを選ぶ',
    description: '「公開」にするとブログ一覧ページにすぐ表示されます。まだ準備中の場合は「下書き」を選んで保存しておきましょう。',
    tips: ['下書きはいつでも公開に切り替えられます'],
  },
  {
    icon: 'ri-text',
    title: 'タイトルを入力する',
    description: '記事の見出しとなるタイトルを入力してください。読者が興味を持てるような、具体的なタイトルをつけましょう。',
    tips: ['タイトルは必須項目です', '20〜40文字程度が読みやすくなります'],
  },
  {
    icon: 'ri-price-tag-3-line',
    title: 'カテゴリー・投稿日を設定する',
    description: 'カテゴリーを選ぶと記事が分類されブログ一覧でフィルターできるようになります。投稿日はDBに設定した日付が表示され、未設定の場合は今日の日付が自動入力されます。',
    tips: ['カテゴリーは「カテゴリー管理」タブから追加できます'],
  },
  {
    icon: 'ri-image-add-line',
    title: 'サムネイル画像をアップロードする',
    description: 'ブログ一覧や記事上部に表示されるメイン画像です。「画像を選択」ボタンからPNG・JPG・WEBPファイルをアップロードしてください。',
    tips: ['推奨サイズ：横1200px × 縦630px（16:9）', '5MB以下のファイルをご使用ください'],
  },
  {
    icon: 'ri-user-line',
    title: '著者情報を入力する',
    description: '著者名と役職を入力します。記事下部に表示される著者プロフィールに使用されます。デフォルト値がすでに入っているので、変更が必要な場合のみ編集してください。',
  },
  {
    icon: 'ri-hashtag',
    title: 'タグを設定する',
    description: '記事に関連するキーワードをカンマ（,）区切りで入力します。読了時間も分単位で設定してください。',
    tips: ['例：婚活Tips, シニア婚活, 成婚体験談', 'タグは検索・分類に活用されます'],
  },
  {
    icon: 'ri-file-text-line',
    title: '概要・本文を書く',
    description: '概要はブログ一覧に表示される短い説明文です（500文字以内）。本文はMarkdown記法が使えます。',
    tips: ['## で見出し、### で小見出しを作れます', '**テキスト** で太字、- でリスト表示になります'],
  },
  {
    icon: 'ri-save-line',
    title: '保存する',
    description: '右下の「公開して保存」または「下書きとして保存」ボタンをクリックして保存します。保存後すぐにブログ一覧に反映されます。',
    tips: ['保存後もいつでも編集・削除できます'],
  },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  post: BlogPost | null;
  categories: BlogCategory[];
  onSaved: () => void;
}

/**
 * DBから取得したtagsをカンマ区切り文字列に正規化する
 * - string[]  → そのままjoin
 * - JSON文字列 → パースしてjoin
 * - 文字列     → そのまま返す
 */
/** 日付文字列を input[type=date] 用の yyyy-MM-dd 形式に変換 */
function toDateInputValue(dateStr: string | null | undefined): string {
  if (!dateStr) return new Date().toISOString().split('T')[0];
  // すでに yyyy-MM-dd 形式
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  // ISO文字列やタイムゾーン付きの場合は日付部分だけ取り出す
  try {
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }
  } catch {
    // パース失敗時は今日
  }
  return new Date().toISOString().split('T')[0];
}

function normalizeTags(tags: unknown): string {
  if (!tags) return '';
  // すでに配列の場合
  if (Array.isArray(tags)) {
    // 各要素がJSON文字列の場合（例: ['["シニア婚活"]'）も再帰的に展開
    const flat = tags.flatMap((t) => {
      if (typeof t === 'string') {
        try {
          const parsed = JSON.parse(t);
          if (Array.isArray(parsed)) return parsed.map(String);
        } catch {
          // JSON以外はそのまま
        }
      }
      return [String(t)];
    });
    return flat.join(', ');
  }
  // JSON文字列の場合（例: '["シニア婚活"]'）
  if (typeof tags === 'string') {
    try {
      const parsed = JSON.parse(tags);
      if (Array.isArray(parsed)) return normalizeTags(parsed);
    } catch {
      // 通常の文字列
    }
    return tags;
  }
  return '';
}

const EMPTY_FORM: BlogPostFormData = {
  title: '',
  excerpt: '',
  content: '',
  thumbnail_url: '',
  category_id: '',
  published_at: new Date().toISOString().split('T')[0],
  author_name: '和はは スタッフ',
  author_role: '婚活カウンセラー',
  read_minutes: 5,
  tags: '',
  status: 'published',
};

export default function BlogFormModal({ isOpen, onClose, post, categories, onSaved }: Props) {
  const [form, setForm] = useState<BlogPostFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (post) {
      setForm({
        title: post.title,
        excerpt: post.excerpt ?? '',
        content: post.content ?? '',
        thumbnail_url: post.thumbnail_url ?? '',
        category_id: post.category_id ?? '',
        published_at: toDateInputValue(post.published_at),
        author_name: post.author_name,
        author_role: post.author_role,
        read_minutes: post.read_minutes,
        tags: normalizeTags(post.tags),
        status: post.status ?? 'published',
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setError(null);
  }, [isOpen, post]);

  const handleChange = (field: keyof BlogPostFormData, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleStatusChange = (status: BlogPostStatus) => {
    setForm((prev) => ({ ...prev, status }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      setError('タイトルを入力してください。');
      return;
    }
    setSaving(true);
    setError(null);

    const tagsArray = form.tags
      ? form.tags.split(',').map((t) => t.trim()).filter(Boolean)
      : [];

    const payload = {
      title: form.title.trim(),
      excerpt: form.excerpt.trim() || null,
      content: form.content.trim() || null,
      thumbnail_url: form.thumbnail_url.trim() || null,
      category_id: form.category_id || null,
      published_at: form.published_at || null,
      author_name: form.author_name.trim() || '和はは スタッフ',
      author_role: form.author_role.trim() || '婚活カウンセラー',
      read_minutes: Number(form.read_minutes) || 5,
      tags: tagsArray,
      status: form.status,
      published: form.status === 'published',
      updated_at: new Date().toISOString(),
    };

    let err;
    if (post) {
      ({ error: err } = await supabase
        .from('8a4f04ed_blog_posts')
        .update(payload)
        .eq('id', post.id));
    } else {
      ({ error: err } = await supabase
        .from('8a4f04ed_blog_posts')
        .insert(payload));
    }

    setSaving(false);
    if (err) {
      setError('保存に失敗しました。もう一度お試しください。');
      return;
    }
    onSaved();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <HelpModal
        title="ブログ記事の編集ガイド"
        sections={[{ label: '手順', steps: BLOG_HELP_STEPS }]}
        isOpen={helpOpen}
        onClose={() => setHelpOpen(false)}
      />
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-8 px-4"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-2xl rounded-2xl"
        style={{ background: '#fff', border: '1px solid #e8d8cc' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 rounded-t-2xl"
          style={{ borderBottom: '1px solid #e8d8cc', background: '#fff8f2' }}
        >
          <h2 className="font-bold text-lg" style={{ color: '#4a3728' }}>
            {post ? '記事を編集' : '新規記事を追加'}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setHelpOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold cursor-pointer transition-opacity hover:opacity-80 whitespace-nowrap"
              style={{ background: '#fff0d8', color: '#c4903a', border: '1px solid #f0d8a0' }}
            >
              <span className="flex items-center justify-center w-3.5 h-3.5">
                <i className="ri-question-line text-xs"></i>
              </span>
              使い方
            </button>
            <button
              onClick={onClose}
              className="flex items-center justify-center w-8 h-8 rounded-full transition-colors cursor-pointer"
              style={{ color: '#9b8b7f' }}
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>

        {/* Form Body */}
        <div className="p-6 flex flex-col gap-5">
          {error && (
            <div
              className="px-4 py-3 rounded-lg text-sm font-bold"
              style={{ background: '#fff0f0', color: '#cc4444', border: '1px solid #f8c4c4' }}
            >
              {error}
            </div>
          )}

          {/* Status Toggle */}
          <div
            className="flex items-center justify-between px-4 py-3 rounded-xl"
            style={{ background: form.status === 'published' ? '#f2f8f4' : '#fdf5e8', border: `1.5px solid ${form.status === 'published' ? '#c8e0cf' : '#f0d8a0'}` }}
          >
            <div className="flex items-center gap-2">
              <span className="flex items-center justify-center w-5 h-5">
                <i
                  className={`text-sm ${form.status === 'published' ? 'ri-global-line' : 'ri-draft-line'}`}
                  style={{ color: form.status === 'published' ? '#5a9e6f' : '#c4903a' }}
                ></i>
              </span>
              <span className="text-sm font-bold" style={{ color: form.status === 'published' ? '#5a9e6f' : '#c4903a' }}>
                {form.status === 'published' ? '公開中' : '下書き保存'}
              </span>
              <span className="text-xs" style={{ color: '#9b8b7f' }}>
                {form.status === 'published' ? '— ブログ一覧に表示されます' : '— ブログ一覧には表示されません'}
              </span>
            </div>
            <div
              className="flex rounded-full p-0.5"
              style={{ background: '#e8d8cc' }}
            >
              <button
                type="button"
                onClick={() => handleStatusChange('published')}
                className="px-3 py-1 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap"
                style={{
                  background: form.status === 'published' ? '#5a9e6f' : 'transparent',
                  color: form.status === 'published' ? '#fff' : '#9b8b7f',
                }}
              >
                公開
              </button>
              <button
                type="button"
                onClick={() => handleStatusChange('draft')}
                className="px-3 py-1 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap"
                style={{
                  background: form.status === 'draft' ? '#c4903a' : 'transparent',
                  color: form.status === 'draft' ? '#fff' : '#9b8b7f',
                }}
              >
                下書き
              </button>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-bold mb-1.5" style={{ color: '#4a3728' }}>
              タイトル <span style={{ color: '#cc4444' }}>*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="記事タイトルを入力"
              className="w-full px-4 py-2.5 rounded-lg text-sm outline-none transition-all"
              style={{ border: '1px solid #d4c4ba', background: '#fff', color: '#4a3728' }}
            />
          </div>

          {/* Category & Published Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold mb-1.5" style={{ color: '#4a3728' }}>
                カテゴリー
              </label>
              <select
                value={form.category_id}
                onChange={(e) => handleChange('category_id', e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg text-sm outline-none cursor-pointer"
                style={{ border: '1px solid #d4c4ba', background: '#fff', color: '#4a3728' }}
              >
                <option value="">未設定</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold mb-1.5" style={{ color: '#4a3728' }}>
                投稿日
              </label>
              <input
                type="date"
                value={form.published_at}
                onChange={(e) => handleChange('published_at', e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg text-sm outline-none"
                style={{ border: '1px solid #d4c4ba', background: '#fff', color: '#4a3728' }}
              />
            </div>
          </div>

          {/* Thumbnail — ImageUploader */}
          <div>
            <label className="block text-sm font-bold mb-1.5" style={{ color: '#4a3728' }}>
              サムネイル画像
            </label>
            <ImageUploader
              currentUrl={form.thumbnail_url}
              onChange={(url) => handleChange('thumbnail_url', url)}
            />
          </div>

          {/* Author name & role */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold mb-1.5" style={{ color: '#4a3728' }}>著者名</label>
              <input
                type="text"
                value={form.author_name}
                onChange={(e) => handleChange('author_name', e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg text-sm outline-none"
                style={{ border: '1px solid #d4c4ba', background: '#fff', color: '#4a3728' }}
              />
            </div>
            <div>
              <label className="block text-sm font-bold mb-1.5" style={{ color: '#4a3728' }}>著者役職</label>
              <input
                type="text"
                value={form.author_role}
                onChange={(e) => handleChange('author_role', e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg text-sm outline-none"
                style={{ border: '1px solid #d4c4ba', background: '#fff', color: '#4a3728' }}
              />
            </div>
          </div>

          {/* Read minutes & tags */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold mb-1.5" style={{ color: '#4a3728' }}>読了時間（分）</label>
              <input
                type="number"
                min={1}
                max={60}
                value={form.read_minutes}
                onChange={(e) => handleChange('read_minutes', Number(e.target.value))}
                className="w-full px-4 py-2.5 rounded-lg text-sm outline-none"
                style={{ border: '1px solid #d4c4ba', background: '#fff', color: '#4a3728' }}
              />
            </div>
            <div>
              <label className="block text-sm font-bold mb-1.5" style={{ color: '#4a3728' }}>タグ（カンマ区切り）</label>
              <input
                type="text"
                value={form.tags}
                onChange={(e) => handleChange('tags', e.target.value)}
                placeholder="婚活Tips, シニア婚活"
                className="w-full px-4 py-2.5 rounded-lg text-sm outline-none"
                style={{ border: '1px solid #d4c4ba', background: '#fff', color: '#4a3728' }}
              />
            </div>
          </div>

          {/* Excerpt */}
          <div>
            <label className="block text-sm font-bold mb-1.5" style={{ color: '#4a3728' }}>概要（一覧表示用）</label>
            <textarea
              value={form.excerpt}
              onChange={(e) => handleChange('excerpt', e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="記事の概要を入力（500文字以内）"
              className="w-full px-4 py-2.5 rounded-lg text-sm outline-none resize-none"
              style={{ border: '1px solid #d4c4ba', background: '#fff', color: '#4a3728', lineHeight: '1.7' }}
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-bold mb-1.5" style={{ color: '#4a3728' }}>
              本文
              <span className="ml-2 font-normal text-xs" style={{ color: '#9b8b7f' }}>
                （## 見出し, ### 小見出し, **太字**, - リストが使えます）
              </span>
            </label>
            <textarea
              value={form.content}
              onChange={(e) => handleChange('content', e.target.value)}
              rows={12}
              placeholder="記事の本文を入力してください..."
              className="w-full px-4 py-2.5 rounded-lg text-sm outline-none resize-y"
              style={{
                border: '1px solid #d4c4ba',
                background: '#fff',
                color: '#4a3728',
                lineHeight: '1.7',
                minHeight: '200px',
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-3 px-6 py-4 rounded-b-2xl"
          style={{ borderTop: '1px solid #e8d8cc', background: '#fff8f2' }}
        >
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-lg text-sm font-bold transition-opacity hover:opacity-80 cursor-pointer whitespace-nowrap"
            style={{ background: '#f0e8e0', color: '#6b5a4e' }}
          >
            キャンセル
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-bold text-white transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap disabled:opacity-60"
            style={{ background: form.status === 'published' ? '#5a9e6f' : '#c4903a' }}
          >
            {saving ? (
              <>
                <span className="flex items-center justify-center w-4 h-4">
                  <i className="ri-loader-4-line animate-spin text-sm"></i>
                </span>
                保存中...
              </>
            ) : (
              <>
                <span className="flex items-center justify-center w-4 h-4">
                  <i className="ri-save-line text-sm"></i>
                </span>
                {post
                  ? form.status === 'published' ? '公開して保存' : '下書きとして保存'
                  : form.status === 'published' ? '公開して追加' : '下書きとして追加'
                }
              </>
            )}
          </button>
        </div>
      </div>
    </div>
    </>
  );
}
